import { useMemo, useState } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { BookFormat } from '@masters-club/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { SortableList } from '@/components/dnd';
import type { TranslationLanguage } from '@/components/translations';
import { BookChapterDialog } from './BookChapterDialog';
import {
  getChapterTitle,
  type BookChapterData,
} from '../types';

interface BookChaptersTabProps {
  bookId: number;
  format: BookFormat;
  chapters: BookChapterData[];
  onChange: (chapters: BookChapterData[]) => void;
  languages: TranslationLanguage[];
}

type SortableChapter = Omit<BookChapterData, 'id'> & { id: string };

function formatDuration(sec: number | null): string {
  if (sec === null) { return '—'; }
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Таб глав книги. Поддерживает drag-to-reorder, создание/редактирование/удаление.
 * Для AUDIO — длительность и аудиофайл; для текста — диапазон страниц.
 */
export function BookChaptersTab({
  bookId,
  format,
  chapters,
  onChange,
  languages,
}: BookChaptersTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<BookChapterData | null>(null);
  const reorderMutation = useCustomMutation();

  const sortableItems = useMemo<SortableChapter[]>(
    () =>
      [...chapters]
        .sort((a, b) => a.order - b.order)
        .map((c, idx) => ({ ...c, id: c.id !== undefined ? `id-${c.id}` : `new-${idx}-${c.order}` })),
    [chapters],
  );

  const handleOpenCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (chapter: SortableChapter) => {
    const { id: _id, ...rest } = chapter;
    const chapterData: BookChapterData = { ...rest, id: _id.startsWith('id-') ? Number(_id.slice(3)) : undefined };
    setEditing(chapterData);
    setDialogOpen(true);
  };

  const handleDelete = (chapter: SortableChapter) => {
    const numericId = chapter.id.startsWith('id-') ? Number(chapter.id.slice(3)) : undefined;
    const next = chapters.filter((c) =>
      numericId !== undefined ? c.id !== numericId : c.order !== chapter.order,
    );
    onChange(next);
  };

  const handleSave = (saved: BookChapterData) => {
    if (editing === null) {
      onChange([...chapters, saved]);
    } else {
      onChange(chapters.map((c) => (c === editing ? saved : c)));
    }
  };

  const handleReorder = (reordered: SortableChapter[]) => {
    const next = reordered.map((item, idx) => {
      const { id: _id, ...rest } = item;
      return { ...rest, order: idx };
    });
    onChange(next);

    // Синхронизация порядка с бэкендом (если все главы уже сохранены).
    const allPersisted = reordered.every((c) => c.id.startsWith('id-'));
    if (allPersisted && bookId > 0) {
      reorderMutation.mutate({
        url: `/admin/books/${bookId}/chapters/reorder`,
        method: 'post',
        values: {
          chapterIds: reordered
            .map((c) => c.id.replace(/^id-/, ''))
            .filter(Boolean),
        },
      });
    }
  };

  const nextOrder = chapters.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Главы</h3>
          <p className="text-sm text-muted-foreground">
            {format === BookFormat.AUDIO
              ? 'Аудио-главы с длительностью и HLS'
              : 'Оглавление: диапазоны страниц'}
          </p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Добавить главу
        </Button>
      </div>

      {sortableItems.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          Главы ещё не добавлены
        </div>
      ) : (
        <SortableList
          items={sortableItems}
          onReorder={handleReorder}
          renderItem={({ item, dragHandleProps }) => {
            const audioBadge = item.hlsPlaylistUrl ? (
              <Badge variant="secondary" className="bg-green-500/10 text-green-600 text-xs">
                HLS готов
              </Badge>
            ) : item.audioFileId ? (
              <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600 text-xs">
                Аудио загружено
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">Нет аудио</Badge>
            );

            return (
              <div className="flex items-center gap-3 rounded-md border bg-card p-3">
                <div {...dragHandleProps} className="cursor-grab text-muted-foreground">
                  <GripVertical className="h-4 w-4" />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {item.order + 1}. {getChapterTitle(item)}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    {format === BookFormat.AUDIO ? (
                      <>
                        <Badge variant="outline" className="text-xs">
                          {formatDuration(item.durationSec)}
                        </Badge>
                        {audioBadge}
                      </>
                    ) : (
                      <span>
                        Стр. {item.pageStart ?? '—'} – {item.pageEnd ?? '—'}
                      </span>
                    )}
                  </div>
                </div>

                <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(item)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          }}
        />
      )}

      <BookChapterDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        chapter={editing}
        nextOrder={nextOrder}
        format={format}
        languages={languages}
        onSave={handleSave}
      />
    </div>
  );
}
